import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { IEntity } from '@proximus/entity-manager';
import { PasswordRecoveryManagement } from '../../models/password-recovery-management.model';
import * as RecoveryMethodsActions from '../actions/password-recovery-management.actions';
import { ApplicationState, getSelectedMailbox } from '../../../store/reducers';
import { PasswordService } from '../../service/password.service';
import { TechnicalError } from '../../models/errors.model';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { BrandService } from 'src/app/services/brand.service';

@Injectable()
export class PasswordRecoveryManagementEffects {
  @Effect()
  passwordRecoveryManagement$: Observable<Action> = this.actions$.pipe(
    ofType(RecoveryMethodsActions.ActionTypes.FIND_RECOVERY_METHODS),
    withLatestFrom(this.store.select(getSelectedMailbox)),
    switchMap(([action, selectedMailbox]) =>
      this.passwordService
        .getPasswordRecoveryManagement(selectedMailbox.mbxId, this.brandService.getAppBrandValue())
        .pipe(
          map(
            (success: IEntity<PasswordRecoveryManagement>) =>
              new RecoveryMethodsActions.FoundRecoveryMethodsAction(
                success.value,
              ),
          ),
          catchError((error: IEntity<TechnicalError>) =>
            of(
              new RecoveryMethodsActions.FindRecoveryMethodsFailedAction(
                error.value,
              ),
            ),
          ),
        ),
    ),
  );

  @Effect()
  setEmailRecoveryMethod$: Observable<Action> = this.actions$.pipe(
    ofType(RecoveryMethodsActions.ActionTypes.SET_EMAIL_RECOVERY_METHOD),
    map(
      (action: RecoveryMethodsActions.SetEmailRecoveryMethodAction) =>
        action.payload,
    ),
    withLatestFrom(this.store.select(getSelectedMailbox)),
    switchMap(([action, mailbox]) =>
      this.passwordService
        .setEmailPasswordRecoveryMethod(mailbox.mbxId, action, this.brandService.getAppBrandValue())
        .pipe(
          map(
            (success: IEntity<any>) =>
              new RecoveryMethodsActions.SetEmailRecoveryMethodSuccessAction(
                action,
              ),
          ),
          catchError((error: IEntity<TechnicalError>) => {
            const errorValue: TechnicalError = error.errorData
              ? { code: error.errorData.code, message: error.errorData.message }
              : error.value;
            return of(
              new RecoveryMethodsActions.SetEmailRecoveryMethodFailAction(
                errorValue,
              ),
            );
          }),
        ),
    ),
  );

  @Effect()
  setSmsRecoveryMethod$: Observable<Action> = this.actions$.pipe(
    ofType(RecoveryMethodsActions.ActionTypes.SET_SMS_RECOVERY_METHOD),
    map(
      (action: RecoveryMethodsActions.SetSmsRecoveryMethodAction) =>
        action.payload,
    ),
    withLatestFrom(this.store.select(getSelectedMailbox)),
    switchMap(([action, mailbox]) =>
      this.passwordService
        .setSmsPasswordRecoveryMethod(mailbox.mbxId, action, this.brandService.getAppBrandValue())
        .pipe(
          map(
            (success: IEntity<any>) =>
              new RecoveryMethodsActions.SetSmsRecoveryMethodSuccessAction(
                action,
              ),
          ),
          catchError((error: IEntity<TechnicalError>) =>
            of(
              new RecoveryMethodsActions.SetSmsRecoveryMethodFailAction(
                error.value,
              ),
            ),
          ),
        ),
    ),
  );

  @Effect()
  activateSmsRecoveryMethod$: Observable<Action> = this.actions$.pipe(
    ofType(RecoveryMethodsActions.ActionTypes.ACTIVATE_SMS_RECOVERY_METHOD),
    map(
      (action: RecoveryMethodsActions.ActivateSmsRecoveryMethodAction) =>
        action.payload,
    ),
    withLatestFrom(this.store.select(getSelectedMailbox)),
    switchMap(([action, mailbox]) =>
      this.passwordService
        .activateSmsPasswordRecoveryMethod(mailbox.mbxId, action, this.brandService.getAppBrandValue())
        .pipe(
          map(
            (success: IEntity<any>) =>
              new RecoveryMethodsActions.ActivateSmsRecoveryMethodSuccessAction(
                action,
              ),
          ),
          catchError((error: IEntity<TechnicalError>) =>
            of(
              new RecoveryMethodsActions.ActivateSmsRecoveryMethodFailAction(
                error.value,
              ),
            ),
          ),
        ),
    ),
  );

  constructor(
    private actions$: Actions,
    private store: Store<ApplicationState>,
    private passwordService: PasswordService,
    private readonly brandService: BrandService,
  ) {}
}
